import { Component, OnInit, ViewContainerRef } from "@angular/core";
import { PaginatedContent } from "../../../../api/common.types";
import { NzTableQueryParams } from "ng-zorro-antd/table";
import { NzModalService } from "ng-zorro-antd/modal";
import { TranslateService } from "@ngx-translate/core";
import { MaturityAssessment, MaturityAssessmentStatus } from "../../../../api/assessment/assessment.types";
import { AdminAssessmentCreateComponent } from "./admin-assessment-create/admin-assessment-create.component";
import { AssessmentService } from "../../../../api/assessment/assessment.service";
import { AccountFilter } from "../../home-admin-data/account.types";
import { take } from "rxjs";
import { DocumentService } from "../../../../api/document/document.service";
import { compareDesc } from "date-fns";
import { AdminAssessmentHashtagsEditComponent } from "./admin-assessment-hashtags-edit/admin-assessment-hashtags-edit.component";
import { TableStateManager } from "../../utils/table-state-manager";

@Component({
  selector: "mh-admin-assessments-list",
  templateUrl: "./admin-assessment-list.component.html",
  styleUrls: ["./admin-assessment-list.component.less"],
})
export class AdminAssessmentListComponent implements OnInit {
  tableState = new TableStateManager<MaturityAssessment>((filter) => this.assessmentService.loadMaturityAssessmentList(filter), (response) => {
    response.items.sort((a, b) => compareDesc(a.createdAt, b.createdAt));
  });

  constructor(
    private modal: NzModalService,
    private readonly translateService: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private assessmentService: AssessmentService,
    private documentService: DocumentService,
  ) {}

  ngOnInit() {
    this.fetchAssessmentList(this.getDefaultQueryParams());
  }

  fetchAssessmentList(filter: AccountFilter) {
    this.tableState.fetchData(filter);
  }

  onPageChange(pageIndex: number): void {
    this.tableState.onPageChange(pageIndex, () => ({}));
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.tableState.onQueryParamsChange(params, () => ({}));
  }

  onQueryChange(filter: AccountFilter) {
    this.tableState.onQueryChange(filter);
  }

  confirmCompleteAssessment(assessment: MaturityAssessment) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("confirm.complete.label"),
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.completeAssessment(assessment.id),
    });
  }

  completeAssessment(assessmentId: string) {
    this.assessmentService.complete(assessmentId).subscribe((success) => {
      if (success && this.tableState.data?.items) {
        const listUpdate = [...this.tableState.data.items];
        const indexToUpdate = listUpdate.findIndex((item) => item.id === assessmentId);
        if (indexToUpdate !== -1) {
          listUpdate[indexToUpdate] = { ...listUpdate[indexToUpdate], status: MaturityAssessmentStatus.COMPLETED };
        }
        this.tableState.data.items = listUpdate;
      }
    });
  }

  confirmUncompleteAssessment(assessment: MaturityAssessment) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("confirm.uncomplete.label"),
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.uncompleteAssessment(assessment.id),
    });
  }

  uncompleteAssessment(assessmentId: string) {
    this.assessmentService.uncomplete(assessmentId).subscribe((success) => {
      if (success && this.tableState.data?.items) {
        const listUpdate = [...this.tableState.data.items];
        const indexToUpdate = listUpdate.findIndex((item) => item.id === assessmentId);
        if (indexToUpdate !== -1) {
          listUpdate[indexToUpdate] = { ...listUpdate[indexToUpdate], status: MaturityAssessmentStatus.ACTIVE };
        }
        this.tableState.data.items = listUpdate;
      }
    });
  }

  confirmDeleteReport(assessment: MaturityAssessment) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("confirm.assignment.delete.report"),
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.deleteReport(assessment.id),
    });
  }

  deleteReport(assessmentId: string) {
    this.assessmentService.deleteReport(assessmentId).subscribe((success) => {
      if (success && this.tableState.data?.items) {
        const listUpdate = [...this.tableState.data.items];
        const indexToUpdate = listUpdate.findIndex((item) => item.id === assessmentId);
        if (indexToUpdate !== -1) {
          listUpdate[indexToUpdate] = {
            ...listUpdate[indexToUpdate],
            resultFile: undefined,
            status: MaturityAssessmentStatus.COMPLETED,
          };
        }
        this.tableState.data.items = listUpdate;
      }
    });
  }

  confirmDeleteAssessment(assessment: MaturityAssessment) {
    this.modal.confirm({
      nzTitle: this.translateService.instant("confirm.delete.label"),
      nzOkText: this.translateService.instant("ok.button"),
      nzCancelText: this.translateService.instant("cancel.button"),
      nzOkType: "primary",
      nzOkDanger: true,
      nzOnOk: () => this.deleteAssessment(assessment.id),
    });
  }

  deleteAssessment(assessmentId: string) {
    this.assessmentService.delete(assessmentId).subscribe((success) => {
      if (success && this.tableState.data?.items) {
        this.fetchAssessmentList(this.getDefaultQueryParams());
      }
    });
  }

  openSetupAssessmentModal() {
    const modal = this.modal.create({
      nzContent: AdminAssessmentCreateComponent,
      nzMaskClosable: false,
      nzViewContainerRef: this.viewContainerRef,
      nzWidth: 800,
    });

    modal.afterClose.subscribe((result: MaturityAssessment) => {
      if (result) {
        this.fetchAssessmentList(this.getDefaultQueryParams());
      }
    });
  }

  downloadAssessmentCSV(assessment: MaturityAssessment) {
    this.assessmentService.getMaturityAssessmentResults(assessment).subscribe();
  }

  downloadAllAssessmentCSV() {
    this.assessmentService.getAllMaturityAssessmentResults().subscribe();
  }

  filesSelected(fileList: FileList | null, assessment: MaturityAssessment) {
    if (fileList?.length) {
      this.uploadReport(fileList, assessment);
    }
  }

  uploadReport(fileList: FileList, assessment: MaturityAssessment) {
    if (fileList?.length) {
      const formData = new FormData();
      const file = fileList[0];
      formData.append("file", file, file.name);
      formData.append("maturityAssessmentId", assessment.id);
      this.documentService.uploadMAReport(formData).subscribe((response) => {
        if (response.resultFile && this.tableState.data?.items) {
          const listUpdate = [...this.tableState.data.items];
          const indexToUpdate = listUpdate.findIndex((item) => item.id === assessment.id);
          if (indexToUpdate !== -1) {
            listUpdate[indexToUpdate] = response;
          }
          this.tableState.data.items = listUpdate;
        }
      });
    }
  }

  editHashtags(assessment: MaturityAssessment) {
    this.openEditHashtagsModal(assessment);
  }

  openEditHashtagsModal(assessment: MaturityAssessment) {
    const modal = this.modal.create({
      nzContent: AdminAssessmentHashtagsEditComponent,
      nzViewContainerRef: this.viewContainerRef,
      nzWidth: 800,
      nzData: { assessment },
      nzMaskClosable: false,
    });

    modal.afterClose.subscribe((result: MaturityAssessment) => {
      if (result && this.tableState.data?.items) {
        console.log("result", result);
      }
    });
  }

  getDefaultQueryParams() {
    return {
      paging: {
        page: 1,
        itemsOnPage: 50,
      },
      searchTerm: "",
    };
  }

  onCopyLinkClick(listEntry: MaturityAssessment) {
    listEntry.linkCopied = true;
    setTimeout(() => (listEntry.linkCopied = false), 2000);
  }

  protected readonly MaturityAssessmentStatus = MaturityAssessmentStatus;
  protected readonly setTimeout = setTimeout;
}
